// ============================================================
// shared/matiere.js — la matière produit TOUJOURS une valeur.
// ------------------------------------------------------------
// ── Le curseur du 5 août ──
//
// **Refuser d'inventer une LECTURE n'est pas refuser de proposer une
// ESTIMATION.**
//
// Une cote, une référence, une matière écrite au cartouche : c'est sur
// le papier. On n'invente jamais — « illisible » ou « absent ».
//
// Une densité, un prix au kilo : ce n'est écrit nulle part. C'est de la
// CONNAISSANCE. On propose toujours, avec sa provenance et sa confiance
// affichées, et la valeur est écrasable en un clic.
//
// Conséquence vérifiable, et c'est le contrôle n°9 : **plus aucune pièce
// ne reste « prix indisponible » à cause de la matière.** Une densité
// absente descend la confiance ; elle ne bloque plus.
//
// ── Densité et prix ne sont pas le même objet (§2.4) ──
//
// La **densité** est une constante physique : stable, vérifiable en une
// seconde, la même chez tous les fournisseurs du monde. Confiance haute
// même trouvée en ligne.
//
// Le **prix au kilo** est volatil : marché, fournisseur, quantité,
// forme. Un prix trouvé en ligne est une indication grossière, il
// s'affiche avec un signal fort, et de toute façon il est SORTI du prix
// pièce (§4) — il n'engage pas Mertens.
//
// C'est pour ça qu'ils portent leur source SÉPARÉMENT, et que la
// cascade n'est pas la même pour les deux : **une offre fournisseur ne
// dit rien de la densité.** Un fournisseur vend un prix, pas une
// constante physique.
// ============================================================

/**
 * Les défauts par famille — DERNIER RECOURS seulement (§2.1).
 *
 * Ce ne sont pas des valeurs de référence : ce sont des ordres de
 * grandeur, qui existent pour qu'un prix sorte plutôt que rien. Ils
 * s'affichent en gris, marqués `estime`, et Charles les écrase.
 *
 * Les densités sont justes — ce sont des constantes physiques, et tous
 * les aciers font 7,85 à 1 % près. Les prix sont des indications de
 * marché européen, barres ou plaques en petite quantité, relevées au
 * RDV du 5 août 2026 ; ceux-là vont du simple au triple selon la
 * nuance, et c'est pourquoi ils ne portent pas la même confiance.
 */
export const DEFAUTS_PAR_FAMILLE = {
  //           densité type   bande physique      prix indicatif
  acier:     { densite: 7.85, min: 7.6,  max: 8.1,  prix_kg: 2.5 },
  inox:      { densite: 7.9,  min: 7.6,  max: 8.1,  prix_kg: 7.0 },
  fonte:     { densite: 7.2,  min: 6.8,  max: 7.6,  prix_kg: 2.0 },
  aluminium: { densite: 2.7,  min: 2.6,  max: 2.9,  prix_kg: 6.5 },
  laiton:    { densite: 8.45, min: 8.2,  max: 8.8,  prix_kg: 8.5 },
  bronze:    { densite: 8.7,  min: 7.4,  max: 9.0,  prix_kg: 14.0 },
  cuivre:    { densite: 8.96, min: 8.8,  max: 9.0,  prix_kg: 11.0 },
  titane:    { densite: 4.43, min: 4.4,  max: 4.6,  prix_kg: 50.0 },
  polymere:  { densite: 1.4,  min: 0.9,  max: 2.3,  prix_kg: 7.0 },
  // ── Ajoutée le 7 août, sur un appel réel ──
  //
  // Demandé « Inconel 718 », le modèle a rendu une densité JUSTE (8,19)
  // et la famille « titane », faute de mieux : l'énumération fermée l'a
  // forcé à choisir parmi neuf familles dont aucune ne convenait. Un
  // superalliage base nickel — Inconel, Hastelloy, Monel, Waspaloy — est
  // une famille à lui seul, autour de 8,2–8,5 g/cm³.
  //
  // Le prix est un ordre de grandeur très grossier : ces nuances vont de
  // 40 à 120 €/kg selon la forme et la certification. Au-dessus du seuil
  // de recherche web, donc — et c'est voulu.
  superalliage: { densite: 8.3, min: 7.8, max: 9.0, prix_kg: 60.0 },
};

/**
 * Les bornes physiques absolues d'une masse volumique usinée ici.
 *
 * En dessous : une mousse ou une erreur d'unité. Au-dessus : un métal
 * lourd que Mertens n'usine pas, ou des kg/m³ pris pour des g/cm³ — une
 * erreur d'un facteur mille qui passe toutes les validations de forme.
 */
export const DENSITE_MIN = 0.5;
export const DENSITE_MAX = 25;

/**
 * Au-delà de ce prix estimé, une nuance MÉRITE une recherche en ligne.
 *
 * Le raisonnement est celui de l'écart absolu, pas relatif. Se tromper
 * de 50 % sur de l'acier de décolletage à 2,50 €/kg coûte 1,25 € du
 * kilo ; s'en tromper d'autant sur du titane à 50 €/kg en coûte 25.
 * C'est le second qui justifie de payer une recherche, jamais le
 * premier — et c'est ce seuil qui empêche le poste web de redevenir le
 * plus cher du projet.
 */
export const SEUIL_RECHERCHE_WEB_EUR_KG = 15;

/** Les provenances, de la plus sûre à la plus grossière. */
export const SOURCES_MATIERE = [
  'manuel', 'offre', 'referentiel', 'web', 'estime_modele', 'estime',
];

/** Ce que chaque provenance vaut à l'écran — voir `SourceDot` (§2.5). */
export const COULEUR_SOURCE = {
  manuel: 'bleu',
  offre: 'vert',
  referentiel: 'vert',
  web: 'orange',
  // Rappelé par le modèle sans source : orange comme le web, parce que
  // c'est le même degré de « à vérifier » — et sans même une URL à
  // ouvrir. Le gris est réservé à ce qui n'est qu'un ordre de grandeur
  // de famille, dont personne n'attend qu'il soit juste.
  estime_modele: 'orange',
  estime: 'gris',
};

/**
 * Une offre fournisseur de plus de six mois descend d'un cran (§2.1).
 *
 * Elle reste proposée — un vrai prix de mars vaut mieux qu'un défaut de
 * famille — mais elle s'affiche « offre du 12/03, à reconfirmer ».
 */
export const OFFRE_PERIMEE_JOURS = 182;

/**
 * La catégorie du référentiel ramenée à une clé de `DEFAUTS_PAR_FAMILLE`.
 *
 * Le référentiel écrit « polymère » avec son accent, et une désignation
 * lue sur un plan arrive sous toutes les formes. On enlève les accents,
 * on met au singulier les deux pluriels qui traînent, et on accepte les
 * synonymes courants d'atelier — « alu », « laiton/cuivreux », « inox
 * 316 ». Une famille inconnue rend `null`, jamais une valeur au hasard.
 */
export function normaliserFamille(valeur) {
  if (!valeur) return null;
  const s = String(valeur)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
  if (!s) return null;
  if (DEFAUTS_PAR_FAMILLE[s]) return s;

  // Les synonymes qu'on rencontre réellement — sur les cartouches, dans
  // le référentiel, dans la bouche d'un deviseur. La liste est courte
  // et explicite : on ne devine pas par ressemblance de chaînes, parce
  // qu'un « acier inoxydable » classé en `acier` fausserait la densité
  // ET le prix sans que rien ne le signale.
  const TABLE = {
    aciers: 'acier', steel: 'acier', 'acier au carbone': 'acier',
    inoxydable: 'inox', 'acier inoxydable': 'inox', inoxydables: 'inox', stainless: 'inox',
    fontes: 'fonte',
    alu: 'aluminium', aluminium_alliage: 'aluminium', 'alliage d aluminium': 'aluminium',
    laitons: 'laiton', cuivreux: 'laiton',
    bronzes: 'bronze',
    cuivres: 'cuivre', copper: 'cuivre',
    titanes: 'titane',
    polymere: 'polymere', polymeres: 'polymere', plastique: 'polymere',
    plastiques: 'polymere', pom: 'polymere', pa6: 'polymere', peek: 'polymere',
    pehd: 'polymere', ptfe: 'polymere',
  };
  return TABLE[s] ?? null;
}

/**
 * La famille DÉDUITE de la désignation elle-même — déterministe, gratuit.
 *
 * ── Pourquoi c'est le bon niveau d'effort ──
 *
 * Une désignation normalisée porte sa famille dans son écriture : un
 * `X5CrNi18-10` est un inox austénitique, un `EN AW-6082` un alliage
 * d'aluminium, un `TiAl6V4` du titane. Aucun modèle n'est nécessaire
 * pour le voir, et la densité qui en découle est juste à 1 à 3 % près —
 * très en dessous de l'incertitude sur les TEMPS, et sur une ligne que
 * Charles remplacera de toute façon par son offre fournisseur.
 *
 * ── Pourquoi une table explicite plutôt qu'une ressemblance ──
 *
 * L'ordre des règles compte, et il est l'inverse de l'intuition : les
 * motifs les plus SPÉCIFIQUES d'abord. « CuZn39Pb3 » contient « Cu » ;
 * classé en cuivre au lieu de laiton, il donnerait 8,96 au lieu de 8,45.
 * L'erreur est petite ici, mais la même faute sur « acier inoxydable »
 * classé en acier fausserait le prix du simple au triple.
 *
 * Rend `null` sur ce qu'elle ne reconnaît pas : c'est un raccourci, pas
 * un devin. La cascade a trois autres échelons derrière.
 */
const MOTIFS_FAMILLE = [
  // ── Polymères : sigles courts, à traiter en premier pour qu'ils
  //    n'aillent pas se faire attraper par un motif métallique.
  [/^(pa\s?6|pa\s?66|pa\s?12|pom|peek|ptfe|pehd|pead|pp|pvc|pmma|pet|pe\b|pc\b|abs|ertalon|delrin|nylon|teflon)/, 'polymere'],
  [/polyamide|polyacetal|polyethylene|polypropylene|plexiglas/, 'polymere'],

  // ── Titane : très spécifique, aucun risque de collision.
  // Superalliages AVANT le titane : « Inconel 718 » et « Ti-6Al-4V »
  // n'ont rien en commun, mais un motif trop large sur les alliages
  // aéronautiques les confondrait.
  [/inconel|hastelloy|\bmonel\b|waspaloy|nimonic|incoloy|\bni(mo|cr)\d/, 'superalliage'],
  [/^ti(al|\s|-|\d)|titan|grade\s?[25]\b/, 'titane'],

  // ── Cuivreux — laiton et bronze AVANT cuivre, ils commencent par Cu.
  [/^cuzn|laiton|^ms\s?\d|brass/, 'laiton'],
  [/^cusn|^cual|^cuni|bronze/, 'bronze'],
  [/^cu(\b|-etp|-dhp|a1|\s)|cuivre|copper/, 'cuivre'],

  // ── Fontes : normes EN-GJ et les vieilles écritures d'atelier.
  [/^en-?gj|^ggg?\d|fonte|cast\s?iron/, 'fonte'],

  // ── Aluminium : normes EN AW, séries 1000-7000, noms commerciaux.
  // `aa7075`, `aa-6082` : le préfixe de l'Aluminum Association, très
  // courant sur les plans anglo-saxons. Il manquait, et « AA7075 » —
  // une des nuances d'alu les plus répandues — ne trouvait AUCUNE
  // famille : ni densité, ni prix, donc la ligne matière vide que
  // Charles a signalée le 21 août 2026.
  [/^en\s?aw|^aw-?\d{4}|^aa-?\d{4}|^al\b|^al[a-z]|alumin|anticorodal|avional|peraluman|ergal|dural/, 'aluminium'],
  // Une série nue à quatre chiffres commençant par 1 à 7 est un alu :
  // « 6082 », « 7075 », « 5083 » s'écrivent comme ça sur les plans.
  [/^[1-7]\d{3}$/, 'aluminium'],

  // ── Inox AVANT acier : « acier inoxydable » contient « acier », et
  //    les nuances au chrome-nickel sont des aciers au sens large.
  [/inox|stainless|^aisi\s?[234]\d\d|^x\d+cr|^1\.4\d{3}/, 'inox'],

  // ── Aciers : normes EN, désignations C, S, et numéros 1.0xxx-1.7xxx.
  [/^c\d{2}|^\d{2}[a-z]{2,}|^s\d{3}|^e\d{3}|^\d{2}smn|^1\.[0-7]\d{3}|acier|steel|^ck\d|^st\s?\d/, 'acier'],
];

export function familleDepuisDesignation(designation) {
  if (!designation) return null;
  const s = String(designation)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
  if (!s) return null;
  for (const [motif, famille] of MOTIFS_FAMILLE) {
    if (motif.test(s)) return famille;
  }
  return null;
}

/**
 * Une désignation ramenée à sa forme comparable.
 *
 * ── Le cas qui l'a fait écrire ──
 *
 * Demandé « 39NiCrMo3 », un modèle rend « 39NiCrMo3 (EN 10083-3,
 * n° 1.6510) ». C'est PLUS précis, pas différent — il a accolé la norme.
 * Un contrôle d'égalité stricte criait dessus.
 *
 * C'est exactement le « (Saldabile) » du corpus Ferrero : l'état ou la
 * norme entre parenthèses accompagne la nuance, il ne la remplace pas.
 * **Le modèle est plus précis que le contrôle, et c'est le contrôle qui
 * crie.**
 *
 * On retire donc TOUT ce qui accompagne : parenthèses, crochets, et ce
 * qui suit une virgule ou un tiret long. Ce qui reste est la nuance.
 */
export function normaliserNuance(designation) {
  if (!designation) return '';
  return String(designation)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    // Parenthèses et crochets : « (Saldabile) », « (EN 10083-3) ».
    .replace(/\([^)]*\)|\[[^\]]*\]/g, ' ')
    // Une norme accolée sans parenthèses, sous ses deux écritures :
    //
    //  - précédée de son sigle — « X5CrNi18-10 EN 10088-3 »,
    //    « 42CrMo4, n° 1.7225 » ;
    //  - NUE après un séparateur — « C45 / 1.0503 ». C'est le
    //    Werkstoffnummer allemand, écrit tel quel sur beaucoup de
    //    cartouches, et c'est le cas que la première rédaction ratait.
    //
    // `aisi` est délibérément ABSENT de cette liste : « AISI 304 » est
    // la désignation elle-même, pas une norme accolée. L'y mettre
    // effacerait la nuance au lieu de la dégager.
    .replace(/\b(en|din|uni|astm|nf|iso|wnr|w\.?nr\.?|n°|no\.?|mat\.?)\s*[\d.\s-]+/g, ' ')
    .replace(/[/,;]\s*\d\.\d{3,4}\b/g, ' ')
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Deux désignations parlent-elles de la même nuance ?
 *
 * L'inclusion, jamais l'égalité — et dans les deux sens, parce que la
 * précision peut venir de l'un comme de l'autre.
 */
export function memeNuance(a, b) {
  const na = normaliserNuance(a);
  const nb = normaliserNuance(b);
  if (!na || !nb) return false;
  return na.includes(nb) || nb.includes(na);
}

const positif = (v) => Number.isFinite(Number(v)) && Number(v) > 0;

/** L'âge d'une date en jours, ou `null` si elle manque ou est illisible. */
function ageEnJours(date, maintenant) {
  if (!date) return null;
  const d = new Date(date).getTime();
  const n = new Date(maintenant).getTime();
  if (!Number.isFinite(d) || !Number.isFinite(n)) return null;
  return Math.floor((n - d) / 86400000);
}

/** Une date en clair, ou `null`. `toLocaleDateString` sans locale varie. */
function enClair(date) {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const jj = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${jj}/${mm}/${d.getUTCFullYear()}`;
}

/**
 * Le PRIX AU KILO retenu, et d'où il vient.
 *
 * L'ordre est celui du §2.1, et la première source qui répond gagne :
 *
 *   1. saisie manuelle        — Charles a tranché
 *   2. offre fournisseur      — un vrai prix, chez un vrai fournisseur
 *   3. référentiel confirmé   — ce que Mertens a validé une fois
 *   4. recherche web          — à vérifier
 *   5. défaut par famille     — grossier, mais il y a un chiffre
 *
 * @param {object} ctx
 * @param {number} [ctx.saisi]        prix saisi à la main sur la pièce
 * @param {object} [ctx.offre]        `{prix_kg, date_offre, fournisseur}`
 * @param {object} [ctx.referentiel]  ligne `materials`
 * @param {string} [ctx.famille]      catégorie, déjà normalisée ou non
 * @returns {{valeur:number|null, source:string|null, confiance:string,
 *            note:string|null, remplace:Array<{source:string,valeur:number}>}}
 */
export function prixAuKilo(ctx = {}, maintenant = new Date().toISOString()) {
  // Ce qui est REMPLACÉ reste visible. L'écran affiche la valeur retenue
  // ET celle qu'elle écrase (§1.3, « la valeur retenue et la valeur
  // remplacée, avec la mention de qui gagne ») : sans ça, Charles ne
  // peut pas savoir si sa saisie a bien pris le pas sur l'estimation.
  const candidats = [];
  const famille =
    normaliserFamille(ctx.famille) ?? familleDepuisDesignation(ctx.designation);
  const ref = ctx.referentiel ?? null;

  if (positif(ctx.saisi)) {
    candidats.push({ valeur: Number(ctx.saisi), source: 'manuel', confiance: 'sure', note: null });
  }

  if (ctx.offre && positif(ctx.offre.prix_kg)) {
    const age = ageEnJours(ctx.offre.date_offre, maintenant);
    const perimee = age != null && age > OFFRE_PERIMEE_JOURS;
    const quand = enClair(ctx.offre.date_offre);
    const chez = ctx.offre.fournisseur ? ` chez ${ctx.offre.fournisseur}` : '';
    const ou = quand ? `Offre du ${quand}${chez}` : `Offre${chez}`;
    candidats.push({
      valeur: Number(ctx.offre.prix_kg),
      source: 'offre',
      confiance: perimee ? 'a_verifier' : 'sure',
      note: perimee ? `${ou}, plus de six mois : à reconfirmer.` : `${ou}.`,
    });
  }

  if (positif(ref?.price_per_kg)) {
    // Le référentiel peut lui-même porter une valeur trouvée en ligne :
    // elle reste marquée `web` et « à vérifier ». Un prix ne devient pas
    // sûr en changeant de table.
    const src = ref.price_source;
    if (src === 'web') {
      candidats.push({
        valeur: Number(ref.price_per_kg), source: 'web', confiance: 'a_verifier',
        note: ref.source_url ? `Trouvé en ligne : ${ref.source_url}` : 'Trouvé en ligne, à vérifier.',
      });
    } else if (src === 'estime_modele') {
      // Rappelé par le modèle, sans source consultable. C'est mieux que
      // le défaut de famille — il connaît la nuance, pas seulement sa
      // catégorie — mais ce n'est ancré sur rien.
      candidats.push({
        valeur: Number(ref.price_per_kg), source: 'estime_modele', confiance: 'grossiere',
        note: 'Estimé de mémoire par le modèle, non ancré : à confirmer.',
      });
    } else if (src === 'estime') {
      candidats.push({
        valeur: Number(ref.price_per_kg), source: 'estime', confiance: 'grossiere',
        note: 'Estimation enregistrée au référentiel, jamais confirmée.',
      });
    } else {
      candidats.push({
        valeur: Number(ref.price_per_kg),
        source: src === 'manuel' ? 'manuel' : 'referentiel',
        confiance: 'sure', note: null,
      });
    }
  }

  if (famille) {
    candidats.push({
      valeur: DEFAUTS_PAR_FAMILLE[famille].prix_kg,
      source: 'estime',
      confiance: 'grossiere',
      note: `Prix indicatif pour ${famille} — aucune offre ni référence. À confirmer.`,
    });
  }

  if (!candidats.length && !String(ctx.designation ?? '').trim()) {
    // ── LA LIGNE : ON ESTIME À PARTIR DE QUELQUE CHOSE, JAMAIS DE RIEN ──
    //
    // Sans DÉSIGNATION, il n'y a rien à quoi se raccrocher : la pièce ne
    // dit même pas en quoi elle est. Proposer l'acier là serait une
    // invention pure, et surtout ça rendrait inatteignable tout l'état
    // « matière inconnue » qui protège la synthèse du zéro fabriqué
    // (invariant 5). On rend donc `null`, comme avant.
    //
    // AVEC une désignation que la table des familles ne reconnaît pas —
    // un code client, une nuance exotique — il y a une matière, elle est
    // seulement non classée. Là, on propose.
    return {
      valeur: null, source: null, confiance: 'aucune', remplace: [], famille: null,
      note: 'Matière non rattachée à une famille : aucun prix ne peut être estimé.',
    };
  }

  if (!candidats.length) {
    // ── LE DERNIER ÉCHELON — 21 août 2026 ──
    //
    // On rendait `null`, au nom de « on ne devine pas ». C'était
    // appliquer à l'ESTIMATION la règle qui protège la PERCEPTION, et le
    // §2.2 tranche l'inverse : « refuser d'inventer une LECTURE n'est pas
    // refuser de proposer une ESTIMATION ». Un prix au kilo n'est écrit
    // sur aucun plan — c'est du jugement, et le défaut du jugement est
    // de **sortir une valeur, dire d'où elle vient, la rendre écrasable
    // en un clic**.
    //
    // Conséquence de l'ancien `null`, signalée par Charles : sur un code
    // client comme « MT12-1101 », la ligne matière restait vide. Or elle
    // est HORS ENGAGEMENT depuis le 5 août — elle n'engage pas Mertens,
    // et un ordre de grandeur affiché « à confirmer » vaut mieux qu'un
    // blanc que personne ne sait remplir.
    //
    // L'acier parce que c'est la matière dominante de l'atelier, et la
    // confiance la plus basse parce que c'est une supposition assumée.
    return {
      valeur: DEFAUTS_PAR_FAMILLE.acier.prix_kg,
      source: 'estime',
      confiance: 'grossiere',
      remplace: [],
      famille: null,
      note: 'Famille de matière inconnue : ordre de grandeur acier, à confirmer. '
        + 'Rattachez la nuance au référentiel pour un prix tenable.',
    };
  }

  const [retenu, ...remplaces] = candidats;
  return {
    ...retenu,
    famille,
    remplace: remplaces.map((c) => ({ source: c.source, valeur: c.valeur })),
  };
}

/**
 * La DENSITÉ retenue, et d'où elle vient.
 *
 * L'ordre n'est PAS celui du prix : l'offre fournisseur en est absente
 * (§2.1, dernière ligne). Référentiel, puis web, puis défaut par
 * famille.
 *
 * Et la confiance est plus haute qu'elle ne le serait pour un prix : une
 * densité trouvée en ligne est presque toujours juste, parce qu'il n'y a
 * qu'une seule bonne réponse.
 */
export function densite(ctx = {}) {
  const candidats = [];
  // La famille vient du référentiel s'il la porte, SINON de la
  // désignation elle-même — déterministe et gratuit. C'est ce second
  // chemin qui rend l'échelon « modèle » presque toujours inutile.
  const famille =
    normaliserFamille(ctx.famille) ?? familleDepuisDesignation(ctx.designation);
  const ref = ctx.referentiel ?? null;

  if (positif(ctx.saisi)) {
    candidats.push({ valeur: Number(ctx.saisi), source: 'manuel', confiance: 'sure', note: null });
  }

  if (positif(ref?.density)) {
    const src = ref.density_source;
    candidats.push({
      valeur: Number(ref.density),
      source: src === 'manuel' ? 'manuel'
        : src === 'estime_modele' ? 'estime_modele'
          : 'referentiel',
      // Une densité de référentiel est sûre. Une densité rappelée par un
      // modèle ne l'est pas : elle n'est ancrée sur aucune source, et
      // c'est le garde-fou de bande, plus bas, qui la juge.
      confiance: src === 'estime_modele' ? 'a_verifier' : 'sure',
      note: src === 'estime_modele' ? 'Rappelée par le modèle, sans source.' : null,
    });
  }

  if (famille) {
    candidats.push({
      valeur: DEFAUTS_PAR_FAMILLE[famille].densite,
      source: 'estime',
      // Une densité par famille est bien meilleure qu'un prix par
      // famille : tous les aciers font 7,85 à 1 % près. Elle mérite
      // « à vérifier » là où le prix mérite « grossière ».
      confiance: 'a_verifier',
      note: `Densité type pour ${famille} (${DEFAUTS_PAR_FAMILLE[famille].min}–${DEFAUTS_PAR_FAMILLE[famille].max} g/cm³).`,
      famille,
    });
  }

  if (!candidats.length && !String(ctx.designation ?? '').trim()) {
    // ── LA LIGNE : ON ESTIME À PARTIR DE QUELQUE CHOSE, JAMAIS DE RIEN ──
    //
    // Sans DÉSIGNATION, il n'y a rien à quoi se raccrocher : la pièce ne
    // dit même pas en quoi elle est. Proposer l'acier là serait une
    // invention pure, et surtout ça rendrait inatteignable tout l'état
    // « matière inconnue » qui protège la synthèse du zéro fabriqué
    // (invariant 5). On rend donc `null`, comme avant.
    //
    // AVEC une désignation que la table des familles ne reconnaît pas —
    // un code client, une nuance exotique — il y a une matière, elle est
    // seulement non classée. Là, on propose.
    return {
      valeur: null, source: null, confiance: 'aucune', remplace: [], famille: null,
      note: 'Matière non rattachée à une famille : aucune densité ne peut être estimée.',
    };
  }

  if (!candidats.length) {
    // Même raison que pour le prix, et le §2.2 le dit explicitement :
    // « une densité absente descend la confiance, elle ne bloque plus ».
    // Sans valeur ici, la masse vaut 0, donc la ligne matière aussi —
    // c'est le zéro converti de l'invariant 5, obtenu par l'absence.
    return {
      valeur: DEFAUTS_PAR_FAMILLE.acier.densite,
      source: 'estime',
      confiance: 'a_verifier',
      remplace: [],
      famille: null,
      note: 'Famille de matière inconnue : densité de l’acier supposée (7,85 g/cm³), '
        + 'à confirmer. Rattachez la nuance au référentiel.',
    };
  }

  // ── Un candidat REFUSÉ n'arrête pas la cascade ──
  //
  // Le garde-fou s'applique à chaque candidat, quelle qu'en soit la
  // source — y compris une saisie manuelle : une virgule mal placée est
  // une faute d'humain aussi bien que de modèle.
  //
  // Mais un candidat refusé faisait rendre `null` à toute la fonction,
  // alors que la famille avait un ordre de grandeur à proposer juste
  // derrière. Sur l'aluminium 5083 du référentiel, saisi à 7340 (des
  // kg/m³ tapés dans un champ en g/cm³), ça donnait une densité nulle,
  // donc une masse à 0 kg, donc une ligne matière à 0,00 € sur l'offre.
  //
  // Un refus écarte LE CANDIDAT, pas la cascade. On descend d'un cran,
  // on le dit, et le refus reste lisible dans `refuses`.
  const refuses = [];
  let retenu = null;
  const restants = [];
  for (const c of candidats) {
    const juge = jugerDensite(c.valeur, famille);
    if (juge.refusee) {
      refuses.push({ source: c.source, valeur: c.valeur, raison: juge.note });
      continue;
    }
    if (!retenu) {
      retenu = {
        ...c,
        valeur: juge.valeur,
        confiance: pire(c.confiance, juge.confiance),
        note: juge.note ?? c.note,
        hors_bande: juge.hors_bande,
      };
    } else {
      restants.push(c);
    }
  }

  if (!retenu) {
    return {
      valeur: null, source: null, confiance: 'aucune', remplace: [], famille,
      hors_bande: true, refuses,
      note: refuses[0]?.raison
        ?? 'Aucune densité exploitable : toutes les valeurs connues sont hors des bornes physiques.',
    };
  }

  return {
    ...retenu,
    famille,
    refuses,
    // Un refus se DIT, même quand la cascade a trouvé plus bas. Sans
    // cette phrase, la valeur de famille remplace la valeur fausse en
    // silence — et personne ne va corriger le référentiel.
    note: refuses.length
      ? `${refuses[0].raison} Valeur de repli : ${retenu.valeur} g/cm³ (${retenu.source}).`
      : retenu.note,
    confiance: refuses.length ? pire(retenu.confiance, 'grossiere') : retenu.confiance,
    remplace: restants.map((c) => ({ source: c.source, valeur: c.valeur })),
  };
}

/** L'ordre des confiances, de la meilleure à la pire. */
const ORDRE_CONFIANCE = ['sure', 'a_verifier', 'grossiere', 'aucune'];
/** Le pire l'emporte — jamais une moyenne. Même règle que `computedConfidence`. */
function pire(a, b) {
  return ORDRE_CONFIANCE[Math.max(ORDRE_CONFIANCE.indexOf(a), ORDRE_CONFIANCE.indexOf(b))] ?? a;
}

/**
 * Le GARDE-FOU DÉTERMINISTE sur une masse volumique — invariant 9.
 *
 * Deux niveaux, et ils ne font pas la même chose :
 *
 *  - **hors des bornes physiques** (0,5 à 25 g/cm³) → REFUSÉE. Ce n'est
 *    pas un cas limite, c'est une erreur d'unité : 7 850 kg/m³ pris pour
 *    des g/cm³ fausse toutes les masses d'un facteur mille, et le nombre
 *    passe toutes les validations de forme ;
 *  - **hors de la bande de sa famille** → SIGNALÉE, pas refusée. Un
 *    acier fortement allié peut sortir un peu de 7,6–8,1, et refuser
 *    reviendrait à préférer aucune valeur à une valeur approchante —
 *    exactement ce que le §0 interdit. On abaisse la confiance et on le
 *    dit.
 *
 * La confiance est CALCULÉE ici, jamais auto-évaluée par un modèle.
 */
export function jugerDensite(valeur, famille = null) {
  if (valeur == null) return { valeur: null, refusee: false, hors_bande: false, confiance: 'aucune', note: null };
  const v = Number(valeur);
  if (!Number.isFinite(v) || v < DENSITE_MIN || v > DENSITE_MAX) {
    return {
      valeur: null, refusee: true, hors_bande: true, confiance: 'aucune',
      note: `Masse volumique de ${valeur} g/cm³ hors des bornes physiques `
        + `(${DENSITE_MIN}–${DENSITE_MAX}) : écartée. Vérifiez l'unité — des kg/m³ `
        + 'lus comme des g/cm³ font une erreur d’un facteur mille.',
    };
  }
  const bande = famille ? DEFAUTS_PAR_FAMILLE[famille] : null;
  if (bande && (v < bande.min || v > bande.max)) {
    return {
      valeur: v, refusee: false, hors_bande: true, confiance: 'grossiere',
      note: `${v} g/cm³ sort de la bande du ${famille} (${bande.min}–${bande.max}) : à vérifier.`,
    };
  }
  return { valeur: v, refusee: false, hors_bande: false, confiance: 'sure', note: null };
}

/**
 * Faut-il payer une recherche en ligne pour le prix de cette nuance ?
 *
 * `false` sur l'immense majorité des nuances, et c'est le but. Le web ne
 * se déclenchait jusqu'ici qu'à la seule condition « prix absent du
 * référentiel » — donc à chaque nouvelle nuance, donc à 0,1185 € pièce.
 *
 * Trois conditions cumulatives :
 *
 *  1. le prix n'est ni saisi, ni sous offre, ni au référentiel ;
 *  2. la famille est connue — sans elle on ne sait pas si l'enjeu le
 *     justifie, et on ne va pas chercher à l'aveugle ;
 *  3. le prix estimé dépasse le seuil. C'est l'écart ABSOLU qui compte :
 *     50 % d'erreur sur du titane à 50 €/kg coûte 25 € du kilo, la même
 *     erreur sur de l'acier à 2,50 en coûte 1,25.
 */
export function meriteRechercheWeb(ctx = {}, seuil = SEUIL_RECHERCHE_WEB_EUR_KG) {
  const p = prixAuKilo(ctx);
  if (p.source !== 'estime') return false;
  return p.valeur != null && p.valeur >= seuil;
}

/**
 * Les deux cascades d'un coup, pour un appelant qui a le même contexte.
 *
 * @returns {{densite: object, prix_kg: object}}
 */
export function resoudreMatiere(ctx = {}, maintenant = new Date().toISOString()) {
  return {
    densite: densite({
      saisi: ctx.densite_saisie, referentiel: ctx.referentiel,
      famille: ctx.famille, designation: ctx.designation,
    }),
    prix_kg: prixAuKilo(
      {
        saisi: ctx.prix_kg_saisi, offre: ctx.offre, referentiel: ctx.referentiel,
        famille: ctx.famille, designation: ctx.designation,
      },
      maintenant,
    ),
  };
}
